import { ComponentType, createContext, useContext } from 'react';

export type WidgetLoginOptions = {
  /** Prefills the popup's e-mail field (`#email=` hash param). */
  email?: string;
  /** The place demands an account; the popup's create-account notice says so (`#requireAccount=1`). */
  requireAccount?: boolean;
};

export type WidgetLoginPanelProps = {
  /** Prefills the e-mail field. */
  prefillEmail?: string;
  /** Renders the "continue as guest" action under the login CTA. */
  continueAsGuest?: () => void;
  /**
   * The panel is mid-login: a code is in flight, or the success beat is playing.
   * The checkout mounts its login card only while a login is required, and the
   * card's own login is what takes that requirement away — so it holds the card
   * mounted while this is true, or the beat goes down with it.
   */
  onBusyChange?: (busy: boolean) => void;
  /**
   * The visitor is logged in and the success beat has played. Whatever opened
   * the panel closes itself here.
   */
  onLoggedIn?: (token: string) => void;
  /** The place demands an account; the create-account notice says so. */
  requireAccount?: boolean;
};

export type WidgetLoginCardProps = Pick<
  WidgetLoginPanelProps,
  'prefillEmail' | 'continueAsGuest' | 'requireAccount' | 'onBusyChange'
>;

export type WidgetLoginContextValue = {
  /**
   * Opens the first-party login popup. Synchronous — call it directly inside
   * the click handler (Safari only honours `window.open` within the gesture).
   */
  openLoginPopup: (options?: WidgetLoginOptions) => void;
  /** A popup is open and its outcome is still unresolved. */
  busy: boolean;
  /**
   * The widget's e-mail OTP login, rendered in place of the checkout's own
   * login card where there is no popup to open (a Linked Session). Undefined
   * on the marketplace, which mounts its own login provider and modal.
   */
  LoginPanel?: ComponentType<WidgetLoginPanelProps>;
  /** The checkout's login card, framed and linked alike. Undefined on the marketplace, which renders its own. */
  LoginCard?: ComponentType<WidgetLoginCardProps>;
  /**
   * Opens the widget's own login panel over the current view. Present only in a
   * Linked Session: framed there is a popup to open instead, and the marketplace
   * mounts its own login modal.
   */
  openLoginPanel?: (options?: WidgetLoginOptions) => void;
};

/**
 * Provided by `WidgetLogin` at the widget's checkout boundary. The default is
 * inert so the checkout components render unchanged on the marketplace.
 * Keep this module light — it is imported by shared checkout components.
 */
export const WidgetLoginContext = createContext<WidgetLoginContextValue>({
  openLoginPopup: () => {},
  busy: false,
});

export const useWidgetLogin = () => useContext(WidgetLoginContext);
